export const DEVELOPMENT_PROJECT_REF = 'rhlehcghpzirxmpxyihl'
export const LOCAL_SUPABASE_ORIGIN = 'http://127.0.0.1:54321'

export type AppEnvironment = 'local' | 'development' | 'production'

export type PublicEnvironment = Readonly<{
  appEnvironment: AppEnvironment
  environmentLabel: 'LOCAL' | 'DEVELOPMENT' | 'PRODUCTION'
  supabaseUrl: string
  supabasePublishableKey: string
  backendIdentity: string
  storageNamespace: string
}>

type EnvironmentSource = Readonly<Record<string, string | undefined>>

const privilegedKeyPatterns = [
  /service[_-]?role/i,
  /^eyJ[\w-]+\.[\w-]+\.[\w-]+$/,
]

export function projectRefFromUrl(url: URL): string {
  const [projectRef] = url.hostname.split('.')
  if (!projectRef)
    throw new Error('Supabase URL must include a project reference.')
  return projectRef
}

export function createStorageNamespace(
  appEnvironment: string,
  projectRef: string,
): string {
  return `outreach-os:${appEnvironment}:${projectRef}`
}

export function parsePublicEnvironment(
  source: EnvironmentSource,
): PublicEnvironment {
  const appEnvironment = source.VITE_APP_ENV
  const rawUrl = source.VITE_SUPABASE_URL
  const publishableKey = source.VITE_SUPABASE_PUBLISHABLE_KEY
  const configuredProjectRef = source.VITE_SUPABASE_PROJECT_REF

  if (appEnvironment !== 'local' && appEnvironment !== 'development' && appEnvironment !== 'production') {
    throw new Error('VITE_APP_ENV must be local, development, or production for this build.')
  }
  if (!rawUrl) throw new Error('VITE_SUPABASE_URL is required.')
  if (!publishableKey)
    throw new Error('VITE_SUPABASE_PUBLISHABLE_KEY is required.')
  if (privilegedKeyPatterns.some((pattern) => pattern.test(publishableKey))) {
    throw new Error(
      'A privileged Supabase key must never be used in the browser.',
    )
  }

  let supabaseUrl: URL
  try {
    supabaseUrl = new URL(rawUrl)
  } catch {
    throw new Error('VITE_SUPABASE_URL must be a valid URL.')
  }

  let backendIdentity: string
  if (appEnvironment === 'local') {
    if (supabaseUrl.origin !== LOCAL_SUPABASE_ORIGIN) {
      throw new Error(
        'Local builds may connect only to the local Supabase stack.',
      )
    }
    backendIdentity = 'local-127-0-0-1-54321'
  } else {
    if (supabaseUrl.protocol !== 'https:') {
      throw new Error('Hosted Supabase URLs must use HTTPS.')
    }
    const projectRef = projectRefFromUrl(supabaseUrl)
    if (appEnvironment === 'development' && projectRef !== DEVELOPMENT_PROJECT_REF) {
      throw new Error(
        'Development builds may connect only to the Development Supabase project.',
      )
    }
    if (appEnvironment === 'production') {
      if (!configuredProjectRef)
        throw new Error('VITE_SUPABASE_PROJECT_REF is required for production builds.')
      if (projectRef !== configuredProjectRef)
        throw new Error('Production builds may connect only to the configured Production Supabase project.')
    }
    backendIdentity = projectRef
  }

  return Object.freeze({
    appEnvironment,
    environmentLabel: appEnvironment === 'local' ? 'LOCAL' : appEnvironment === 'development' ? 'DEVELOPMENT' : 'PRODUCTION',
    supabaseUrl: supabaseUrl.origin,
    supabasePublishableKey: publishableKey,
    backendIdentity,
    storageNamespace: createStorageNamespace(appEnvironment, backendIdentity),
  })
}

export const appConfig = parsePublicEnvironment(import.meta.env)
